import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { makeStyles } from '@mui/styles'
import { IFrameContent } from '@/modules/base/components/modal'
import { urls } from '@/modules/banking/config'
import {
  openBankAccountSetupTanUniversal,
  closeBankAccountSetupTanUniversal,
} from '@/redux/ducks/banking/bankAccountSetup'
import {
  fetchBankAccountList,
} from '@/redux/ducks/banking/bankAccountList'
import { user } from 'ampersand-app'
import { experimentalFeatures } from '@/modules/base/util/constants'

const useStyles = makeStyles(() => ({
  iframeV1: {
    border: 0,
  },
  iframeV2: {
    border: 'none',
    width: '100%',
    height: '100vh',
    backgroundColor: 'transparent',
    backdropFilter: 'blur(2px)',
  },
}))

const fallbackUriV2CssUrl = `${window.location.origin}/css/universal-modal.css`

export const UniversalModal = (props) => {
  const { transaction, onSuccess } = props
  const classes = useStyles(props)
  const dispatch = useDispatch()
  const { bankAccountSetup } = useSelector(state => state.banking)
  const [state, setState] = useState({ useModal: true })
  const { provideUniversalRequest: { fallbackUri, fallbackUriV2, version } } = transaction
  const alwaysUseV2 = user?.experimentalFeatures.includes(experimentalFeatures.universalCallBackV2)

  const content = useMemo(() => {
    let uri = fallbackUri
    const useV2 = (fallbackUriV2 && (alwaysUseV2 || version >= 5))
    if (useV2) {
      uri = `${fallbackUriV2}&customCss=${encodeURIComponent(fallbackUriV2CssUrl)}`
    }
    const iframeSrc = `${urls.bankingApiHost}${uri}`
    if (useV2) {
      return (
        <IFrameContent
          disableEscapeKeyDown
          disableBackdropClick
          open={true}
          fullScreen
          PaperProps={{ style: { background: 'transparent', boxShadow: 'none' } }} >
          <iframe
            className={classes.iframeV2}
            ref={iframeRef}
            src={iframeSrc}
            allowTransparency={true}
          />
        </IFrameContent>
      )
    }
    return (
      <IFrameContent
        disableEscapeKeyDown
        disableBackdropClick
        open={true}>
        <iframe
          className={classes.iframeV1}
          ref={iframeRef}
          src={iframeSrc}
          height="400"
          width="600"
        />
      </IFrameContent>
    )
  }, [fallbackUri, fallbackUriV2, version, alwaysUseV2])

  const iframeRef = useRef(null)

  const handleWindowMessage = (event) => {
    if (event.data === 'StatusChanged') {
      onSuccess(transaction)
    }
  }

  useEffect(() => {
    if (!bankAccountSetup?.showBankAccountSetupView && !dispatch) {
      return
    }
    window.addEventListener('message', handleWindowMessage, false)

    return () => {
      window.removeEventListener('message', handleWindowMessage)
      if (!bankAccountSetup?.showBankAccountSetupView) {
        dispatch(fetchBankAccountList())
      }
    }
  }, [bankAccountSetup])

  useEffect(() => {
    if (
      state.useModal
      && bankAccountSetup
      && bankAccountSetup.showBankAccountSetupView
      && !bankAccountSetup.showBankAccountSetupTanUniversal
    ) {
      dispatch(openBankAccountSetupTanUniversal(transaction.provideUniversalRequest))
      setState({ useModal: false })
    }

    if (
      !state.useModal
      && bankAccountSetup
      && bankAccountSetup.showBankAccountSetupView
    ) {
      switch (true) {
        case !!bankAccountSetup.errorBankAccountSetupTanInput:
          props.onError(props.bankAccountSetup.errorBankAccountSetupTanInput)
          break
        case !!bankAccountSetup.abortBankAccountSetupTanInput:
          props.onAbort()
          break
        case !!bankAccountSetup.successBankAccountSetupTanUniversal:
          props.onSuccess(props.transaction)
          dispatch(closeBankAccountSetupTanUniversal())
          break
      }
    }
  }, [bankAccountSetup, dispatch, props, state.useModal, transaction])

  return (
    state.useModal
      ? content
      : null
  )
}

export default UniversalModal
